import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import type { BankAccountApi } from "@/api/models/bankAccount";
import type { PurchaseTransactionApi } from "@/api/models/purchaseTransaction";
import type { SalesTransactionApi } from "@/api/models/salesTransaction";
import { getAllBankAccounts } from "@/api/services/bankAccountService";
import { getAllPurchaseTransactions } from "@/api/services/purchaseTransactionService";
import { getAllSalesTransactions } from "@/api/services/salesTransactionService";
import { useApp } from "@/contexts/AppContext";
import { useDataSourceContext } from "@/contexts/DataSourceContext";
import { useBaseForecast } from "@/hooks/useBaseForecast";
import type { DashboardMetrics } from "@/services/metricsService";
import { getDashboardMetrics } from "@/services/metricsService";

interface UseDashboardMetricsResult {
	metrics: DashboardMetrics | null;
	isLoading: boolean;
	error: string | null;
	forecastNotReady: boolean;
	bankAccounts: BankAccountApi[];
	salesTransactions: SalesTransactionApi[];
	purchaseTransactions: PurchaseTransactionApi[];
}

export function useDashboardMetrics(): UseDashboardMetricsResult {
	const { mode } = useApp();
	const {
		metrics: forecastMetrics,
		isLoading: forecastLoading,
		error: forecastError,
		forecastNotReady,
	} = useBaseForecast();
	const { dataSources } = useDataSourceContext();
	const isLive = mode === "live";

	const bankAccountsQuery = useQuery({
		queryKey: ["dashboardBankAccounts"],
		queryFn: getAllBankAccounts,
		enabled: isLive,
		staleTime: 5 * 60 * 1000,
	});

	const salesQuery = useQuery({
		queryKey: ["dashboardSalesTransactions"],
		queryFn: () => getAllSalesTransactions(),
		enabled: isLive,
		staleTime: 5 * 60 * 1000,
	});

	const purchaseQuery = useQuery({
		queryKey: ["dashboardPurchaseTransactions"],
		queryFn: () => getAllPurchaseTransactions(),
		enabled: isLive,
		staleTime: 5 * 60 * 1000,
	});

	const demoMetrics = useMemo(() => {
		if (isLive) return null;
		return getDashboardMetrics(dataSources);
	}, [dataSources, isLive]);

	const bankAccounts = useMemo(
		() => bankAccountsQuery.data?.bankAccounts ?? [],
		[bankAccountsQuery.data?.bankAccounts],
	);
	const salesTransactions = useMemo(
		() => salesQuery.data?.salesTransactions ?? [],
		[salesQuery.data?.salesTransactions],
	);
	const purchaseTransactions = useMemo(
		() => purchaseQuery.data?.purchaseTransactions ?? [],
		[purchaseQuery.data?.purchaseTransactions],
	);

	const liveMetrics = useMemo((): DashboardMetrics | null => {
		if (!isLive || !forecastMetrics) return null;

		const now = new Date();
		const ninetyDaysFromNow = new Date(
			now.getTime() + 90 * 24 * 60 * 60 * 1000,
		);

		const currentCash = forecastMetrics.startingCash;
		const projectedCash90Days = forecastMetrics.endingCash;
		const runway = forecastMetrics.coverageMonths ?? 0;
		const expected90DayIncome = forecastMetrics.avgMonthlyInflow * 3;
		const expected90DayExpenses = forecastMetrics.avgMonthlyOutflow * 3;
		const expected30DayIncome = forecastMetrics.avgMonthlyInflow;
		const expected30DayExpenses = forecastMetrics.avgMonthlyOutflow;
		const projectedCash30Days =
			currentCash +
			forecastMetrics.avgMonthlyInflow -
			forecastMetrics.avgMonthlyOutflow;
		const dailyBurn =
			forecastMetrics.avgMonthlyOutflow > 0
				? Math.round(forecastMetrics.avgMonthlyOutflow / 30)
				: 0;

		// Overdue invoices from sales transactions
		const overdueInvoicesList = salesTransactions.filter((txn) => {
			if ((txn.outstanding ?? 0) <= 0) return false;
			const dueDate = new Date(txn.dueDate ?? txn.transactionDate ?? "");
			return dueDate < now;
		});
		const overdueInvoices = overdueInvoicesList.length;
		const overdueAmount = overdueInvoicesList.reduce(
			(sum, txn) => sum + (txn.outstanding ?? 0),
			0,
		);

		// Upcoming payments from purchase transactions
		const upcomingPaymentsList = purchaseTransactions.filter((txn) => {
			if ((txn.outstanding ?? 0) <= 0) return false;
			const effectiveDueDate =
				txn.dueDate || txn.anticipatedPaymentDate || txn.transactionDate;
			const dueDate = new Date(effectiveDueDate ?? "");
			return dueDate >= now && dueDate <= ninetyDaysFromNow;
		});
		const upcomingPayments = upcomingPaymentsList.length;
		const upcomingAmount = upcomingPaymentsList.reduce(
			(sum, txn) => sum + (txn.outstanding ?? 0),
			0,
		);

		// Facility and restriction data from bank accounts
		const serverTotals = bankAccountsQuery.data?.totals;
		let totalRestricted: number;
		let availableCash: number;
		let totalFacilityLimit: number;
		let totalFacilityDrawn: number;
		let totalFacilityHeadroom: number;
		let maxAvailableCash: number;

		if (serverTotals) {
			totalRestricted = serverTotals.totalRestricted;
			availableCash = serverTotals.availableCash;
			totalFacilityLimit = serverTotals.totalFacilityLimit;
			totalFacilityDrawn = serverTotals.totalFacilityDrawn;
			totalFacilityHeadroom = serverTotals.totalFacilityHeadroom;
			maxAvailableCash = serverTotals.maxAvailableCash;
		} else {
			totalRestricted = bankAccounts.reduce((sum, acc) => {
				if (!acc.restrictedFunds) return sum;
				return (
					sum +
					acc.restrictedFunds.reduce((s, r) => s + (r.restrictedAmount || 0), 0)
				);
			}, 0);
			availableCash = currentCash - totalRestricted;
			totalFacilityLimit = bankAccounts.reduce(
				(sum, acc) => sum + (acc.creditFacility?.facilityLimit || 0),
				0,
			);
			totalFacilityDrawn = bankAccounts.reduce(
				(sum, acc) => sum + (acc.creditFacility?.facilityDrawn || 0),
				0,
			);
			totalFacilityHeadroom = totalFacilityLimit - totalFacilityDrawn;
			maxAvailableCash = availableCash + totalFacilityHeadroom;
		}

		return {
			currentCash,
			projectedCash30Days,
			projectedCash90Days,
			runway,
			overdueInvoices,
			overdueAmount,
			upcomingPayments,
			upcomingAmount,
			expected30DayIncome,
			expected30DayExpenses,
			expected90DayIncome,
			expected90DayExpenses,
			weeklyForecast: 0,
			weeklyActual: 0,
			monthlyForecast: 0,
			monthlyActual: 0,
			dailyBurn,
			totalRestricted,
			availableCash,
			totalFacilityLimit,
			totalFacilityDrawn,
			totalFacilityHeadroom,
			maxAvailableCash,
		};
	}, [
		isLive,
		forecastMetrics,
		salesTransactions,
		purchaseTransactions,
		bankAccounts,
		bankAccountsQuery.data?.totals,
	]);

	const isLoading = isLive
		? forecastLoading ||
			bankAccountsQuery.isLoading ||
			salesQuery.isLoading ||
			purchaseQuery.isLoading
		: false;

	const error = isLive
		? ((forecastNotReady ? null : forecastError) ??
			(bankAccountsQuery.error instanceof Error
				? bankAccountsQuery.error.message
				: bankAccountsQuery.error
					? "Failed to load bank accounts"
					: null) ??
			(salesQuery.error instanceof Error
				? salesQuery.error.message
				: salesQuery.error
					? "Failed to load sales transactions"
					: null) ??
			(purchaseQuery.error instanceof Error
				? purchaseQuery.error.message
				: purchaseQuery.error
					? "Failed to load purchase transactions"
					: null))
		: null;

	return {
		metrics: isLive ? liveMetrics : demoMetrics,
		isLoading,
		error,
		forecastNotReady,
		bankAccounts,
		salesTransactions,
		purchaseTransactions,
	};
}
